import { useEffect, useRef, useState } from 'react';
import type { FormatNumberOptions } from 'react-intl';
import { useIntl } from 'react-intl';
import styled, { css, keyframes } from 'styled-components';

import { Vertical } from 'atoms/layout/flex';

const Wrapper = styled.span`
  position: relative;
  font-variant-numeric: tabular-nums;
`;

const previousValueAnimation = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-100%);
    opacity: 0;
  }
`;

const PreviousValue = styled.span`
  transform: translateY(0);
  opacity: 1;
  animation: ${previousValueAnimation} 660ms
    cubic-bezier(0.51, 0.03, 0.29, 0.95) forwards;
`;

const currentValueAnimation = keyframes`
  from {
    transform: translateY(0);
    opacity: 0;
  }
  to {
    transform: translateY(-100%);
    opacity: 1;
  }
`;
const CurrentValue = styled.span`
  transform: translateY(0);
  opacity: 0;
  animation: ${currentValueAnimation} 660ms cubic-bezier(0.51, 0.03, 0.29, 0.95)
    forwards;
`;

const ValueWrapper = styled.span<{ animating: boolean }>`
  ${({ animating }) =>
    animating &&
    css`
      opacity: 0;
    `}
`;

const AnimationBlockWrapper = styled(Vertical).attrs({ center: true, gap: 0 })`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

type Props = {
  value: number;
  formatNumberProps?: FormatNumberOptions;
};

export const SlotMachineAnimatedNumber = ({
  value,
  formatNumberProps,
}: Props) => {
  const { formatNumber } = useIntl();
  const [animating, setAnimating] = useState(false);
  const isFirstRender = useRef(true);
  const [previousValue, setPreviousValue] = useState(value);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return () => {};
    }
    setAnimating(true);
    const timeout = setTimeout(() => {
      setAnimating(false);
      setPreviousValue(value);
    }, 660);
    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <Wrapper>
      <ValueWrapper animating={animating}>
        {formatNumber(value, {
          maximumFractionDigits: 0,
          ...formatNumberProps,
        })}
      </ValueWrapper>
      {!!animating && (
        <AnimationBlockWrapper>
          <PreviousValue>
            {formatNumber(previousValue || 0, {
              maximumFractionDigits: 0,
              ...formatNumberProps,
            })}
          </PreviousValue>
          <CurrentValue>
            {formatNumber(value || 0, {
              maximumFractionDigits: 0,
              ...formatNumberProps,
            })}
          </CurrentValue>
        </AnimationBlockWrapper>
      )}
    </Wrapper>
  );
};
